/** URL-safe slug from a display name. Shared by the tenant wizard and the admin table. */
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * "Severe Thunderstorm Warning" -> "Severe thunderstorm".
 * The card says what the weather is, not what the product is called.
 */
export function humanEvent(event: string | null | undefined): string {
  const s = (event ?? '').replace(/\s+(Warning|Watch|Advisory|Statement)\s*$/i, '').trim();
  if (!s) return 'Weather';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * Hail diameter in inches -> something a person can picture. "1.75 in" means
 * nothing to most readers; "golf ball" means duck.
 */
const HAIL_OBJECTS: Array<[inches: number, object: string]> = [
  [0.25, 'pea'],
  [0.5, 'marble'],
  [0.75, 'penny'],
  [0.88, 'nickel'],
  [1.0, 'quarter'],
  [1.25, 'half dollar'],
  [1.5, 'ping pong ball'],
  [1.75, 'golf ball'],
  [2.0, 'hen egg'],
  [2.5, 'tennis ball'],
  [2.75, 'baseball'],
  [4.0, 'softball'],
];

export function hailObject(inches: number | null | undefined): string | null {
  if (typeof inches !== 'number' || !Number.isFinite(inches) || inches <= 0) return null;
  let best = HAIL_OBJECTS[0]!;
  for (const entry of HAIL_OBJECTS) {
    if (Math.abs(entry[0] - inches) < Math.abs(best[0] - inches)) best = entry;
  }
  return best[1];
}

/** "a quarter to a half inch" reads better than "0.25-0.5 in" for a non-expert. */
function inches(r: { low: number; high: number }): string {
  const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0$/, ''));
  return r.low > 0 && r.high > r.low ? `${fmt(r.low)}–${fmt(r.high)} in` : `up to ${fmt(r.high)} in`;
}

/**
 * "Winds to 70 mph and golf-ball hail (1.75 in)." Null when the alert says none of them.
 *
 * Ice leads when present: for this audience it is the figure that decides whether the
 * power stays on.
 */
export function hazardSentence(
  parameters:
    | {
        maxWindGustMph?: number | null;
        maxHailSizeIn?: number | null;
        iceAccumulationIn?: { low: number; high: number } | null;
        snowAccumulationIn?: { low: number; high: number } | null;
      }
    | null
    | undefined,
): string | null {
  const gust = parameters?.maxWindGustMph;
  const hail = parameters?.maxHailSizeIn;
  const ice = parameters?.iceAccumulationIn;
  const snow = parameters?.snowAccumulationIn;
  const parts: string[] = [];
  if (ice && ice.high > 0) parts.push(`${inches(ice)} of ice`);
  if (snow && snow.high > 0) parts.push(`${inches(snow)} of snow`);
  if (typeof gust === 'number' && gust > 0) parts.push(`winds to ${Math.round(gust)} mph`);
  if (typeof hail === 'number' && hail > 0) {
    const obj = hailObject(hail);
    parts.push(obj ? `${obj}-size hail (${hail.toFixed(2)} in)` : `${hail.toFixed(2)} in hail`);
  }
  if (!parts.length) return null;
  const joined = parts.join(' and ');
  return `Expect ${joined}.`;
}
